import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { IconButton } from '@mui/material';
import { SendIcon } from 'lucide-react';

export default function ChatInput({
  onSend,
  disabled,
  sessionId,
  isDark,
  onNewChat
}) {
  const [text, setText] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  }, [disabled, sessionId]);

  const handleSend = () => {
    if (!text.trim() || disabled) return;
    onSend(text);
    setText('');
  };

  return (
    <Box
      sx={{
        borderTop: '1px solid #cfd8a6',
        p: 2,
        flexShrink: 0,
        background: '#E6F1C1',

      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          size="small"
          value={text}
          disabled={disabled}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={disabled ? 'Please wait…' : 'Type your message…'}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: isDark ? '#242523' : '#FFFFFF',
              borderRadius: 2.5,
              fontSize: '0.95rem',
              transition: 'all 0.25s ease',
              '& fieldset': {
                borderColor: isDark ? '#2E2F2D' : '#C5DE6E',
                borderWidth: 1.5,
              },
              '&:hover fieldset': {
                borderColor: isDark ? '#B1D23A' : '#819C24',
                borderWidth: 2,
              },
              '&.Mui-focused fieldset': {
                borderColor: isDark ? '#B1D23A' : '#819C24',
                borderWidth: 2,
              },
              '& textarea': {
                color: isDark ? '#D9DBD3' : '#252720',
                py: 1.5,
              },
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: isDark ? '#6F745F' : '#A1A591',
              opacity: 1,
            },
          }}
        />
        <Tooltip title="Send message">
          <IconButton
            onClick={handleSend}
            disabled={disabled || !text.trim()}
            sx={{
              bgcolor: isDark ? '#B1D23A' : '#819C24',
              color: '#FFFFFF',
              width: 52,
              height: 52,
              flexShrink: 0,
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 12px rgba(177,210,58,0.4)',
              '&:hover': {
                bgcolor: isDark ? '#C5DE6E' : '#6F745F',
                transform: 'scale(1.08) translateY(-2px)',
                boxShadow: '0 8px 24px rgba(177,210,58,0.6)',
              },
              '&:disabled': {
                bgcolor: isDark ? '#414437' : '#BDC0B2',
                color: isDark ? '#6F745F' : '#FFFFFF',
                transform: 'none',
              },
            }}
          >
            <SendIcon onClick={onNewChat}/>
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
